import { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, LogOut, Home, RefreshCcw, ChevronDown, ChevronRight, ChevronsLeft, Search, GraduationCap, Building2 } from "lucide-react";
import VideoLibraryMegaMenu from "@/components/navigation/VideoLibraryMegaMenu";
import { useSiteSettings, useApplySiteAccent } from "@/lib/useSiteSettings";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import SocialIcons from "@/components/SocialIcons";
import HeaderSearch from "@/components/HeaderSearch";
import { useTranslation } from 'react-i18next';
import { useMenu, type MenuItemNode } from "@/hooks/useMenu";
import { useAuth } from "@/hooks/useAuth";
import { InstitutionPausedNotice } from "@/components/institutions/InstitutionPausedNotice";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useDashboardPreferences } from "@/hooks/useDashboardPreferences";
import { useTheme } from "@/contexts/ThemeContext";

import { ApplicationComingSoonDialog } from "@/components/ApplicationComingSoonDialog";
import AllAccessPlansDialog from "@/components/AllAccessPlansDialog";
import { useHasAllAccess } from "@/hooks/useHasAllAccess";
import { ClassSuggestionButton } from "@/components/student/ClassSuggestionButton";
import { ReferralChip } from "@/components/dashboard/ReferralChip";
import { useUserInstitution } from "@/hooks/useUserInstitution";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";

// Type definitions for menu items
type MenuItem = {
  label: string;
  href: string;
  adminOnly?: boolean;
};

type MenuGroup = {
  label: string;
  type: "link" | "dropdown";
  href?: string;
  items?: MenuItem[];
  adminOnly?: boolean;
  section?: "dashboard" | "frontend";
};

const DashboardHeader = () => {
  const { t, i18n } = useTranslation('admin');
  const { data: siteSettings } = useSiteSettings();
  useApplySiteAccent();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openSubMenus, setOpenSubMenus] = useState<string[]>([]);
  const [comingSoonOpen, setComingSoonOpen] = useState(false);
  const [plansDialogOpen, setPlansDialogOpen] = useState(false);
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);
  const [isLibraryMobileOpen, setIsLibraryMobileOpen] = useState(true);
  const [isFrontendMobileOpen, setIsFrontendMobileOpen] = useState(false);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const libraryCloseTimer = useRef<number | null>(null);
  const { user, userRoles, signOut, isAdminOrStaff } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { layoutDesktop } = useDashboardPreferences();
  const { theme } = useTheme();
  const { data: hasAllAccess } = useHasAllAccess(user?.id);
  const showMobileSubscribe = !!user && !isAdminOrStaff() && !hasAllAccess;

  // Apply the user's theme preference to <html> so the header (and page) render
  // in light/dark even when this header is used outside DashboardLayout (public pages).
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const prev = document.documentElement.getAttribute('data-theme');
    document.documentElement.setAttribute('data-theme', theme);
    return () => {
      if (prev) document.documentElement.setAttribute('data-theme', prev);
      else document.documentElement.removeAttribute('data-theme');
    };
  }, [theme]);


  // Mobile drawer: push #root left, lock body scroll, close on Escape (matches landing Navigation)
  useEffect(() => {
    const root = document.getElementById('root');
    if (!root) return;
    if (isMenuOpen) {
      root.classList.add('menu-push-open');
      document.body.classList.add('dashboard-menu-open');
      // Scroll to top so the close (X) button in the header is visible
      window.scrollTo({ top: 0, behavior: 'smooth' });
      const prevOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      const onKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape') setIsMenuOpen(false);
      };
      window.addEventListener('keydown', onKey);
      return () => {
        root.classList.remove('menu-push-open');
        document.body.classList.remove('dashboard-menu-open');
        document.body.style.overflow = prevOverflow;
        window.removeEventListener('keydown', onKey);
      };
    }
  }, [isMenuOpen]);

  const applicationPaths = ['/student/application', '/student/applications'];

  // Map of section IDs to menu group labels
  const sectionToMenuMap: Record<string, string> = {
    'admin-section': t('dashboard.header.menu.admin_section'),
    'academic-section': t('dashboard.header.menu.academic_section'),
    'services-section': t('dashboard.header.menu.services_section'),
    'operations-section': t('dashboard.header.menu.operations_section'),
  };

  // Map of card IDs to menu items
  const cardToMenuItemMap: Record<string, { label: string; href: string; adminOnly?: boolean }> = {
    'users': { label: t('dashboard.header.menu.users'), href: "/dashboard/users", adminOnly: true },
    'teachers': { label: t('dashboard.header.menu.teachers'), href: "/dashboard/teachers", adminOnly: true },
    'buildings': { label: t('dashboard.header.menu.buildings'), href: "/dashboard/buildings" },
    'products': { label: t('dashboard.header.menu.products'), href: "/dashboard/products", adminOnly: true },
    'coupons': { label: t('dashboard.header.menu.coupons'), href: "/dashboard/coupons", adminOnly: true },
    'kit-funnel': { label: 'Kit Funnel', href: "/dashboard/kit-funnel", adminOnly: true },
    'widgets': { label: t('dashboard.header.menu.widgets'), href: "/dashboard/lab", adminOnly: true },
    'suscripciones': { label: t('dashboard.header.menu.subscriptions'), href: "/dashboard/enrollments" },
    'analytics': { label: t('dashboard.header.menu.analytics'), href: "/dashboard/analytics", adminOnly: true },
    'permissions': { label: t('dashboard.header.menu.permissions'), href: "/dashboard/permissions", adminOnly: true },
    'social-networks': { label: t('dashboard.header.menu.social_networks', 'Social Networks'), href: "/dashboard/social-networks", adminOnly: true },
    'students': { label: t('dashboard.header.menu.students'), href: "/dashboard/students" },
    'programas': { label: t('dashboard.header.menu.programs'), href: "/dashboard/programs", adminOnly: true },
    'clases-online': { label: t('dashboard.header.menu.classes_courses'), href: "/dashboard/classes-courses", adminOnly: true },
    'clases-presenciales': { label: t('dashboard.header.menu.classes_courses'), href: "/dashboard/classes-courses", adminOnly: true },
    'cohorts': { label: t('dashboard.header.menu.generations'), href: "/dashboard/cohorts", adminOnly: true },
    'attendance': { label: t('dashboard.header.menu.attendance'), href: "/dashboard/attendance" },
    'solicitudes-programa': { label: t('dashboard.header.menu.program_applications'), href: "/dashboard/applications" },
    'solicitudes-clases': { label: t('dashboard.header.menu.class_requests'), href: "/dashboard/course-enrollment-requests" },
    'grades': { label: t('dashboard.header.menu.grades'), href: "/dashboard/grades" },
    'classes-courses': { label: t('dashboard.header.menu.classes_courses'), href: "/dashboard/classes-courses" },
    'leads': { label: t('dashboard.header.menu.leads'), href: "/dashboard/leads" },
    'careers': { label: t('dashboard.header.menu.careers', 'Careers'), href: "/dashboard/careers", adminOnly: true },
    'tasks': { label: t('dashboard.header.menu.tracking'), href: "/dashboard/tasks", adminOnly: true },
    'calendar': { label: t('dashboard.header.menu.calendar'), href: "/dashboard/school-calendar" },
    'communications': { label: t('dashboard.header.menu.communications'), href: "/dashboard/communications" },
    'presentations': { label: t('dashboard.header.menu.presentations'), href: "/dashboard/presentations", adminOnly: true },
    'calendar-events': { label: t('dashboard.header.menu.events'), href: "/dashboard/calendar-events" },
    'pop-ups-announcements': { label: t('dashboard.header.menu.announcements', 'Pop-ups Announcements'), href: "/dashboard/announcements", adminOnly: true },
    'pdf-library': { label: t('dashboard.header.menu.pdf_library', 'PDF Library'), href: "/dashboard/pdf-library", adminOnly: true },
    'press-releases': { label: t('dashboard.header.menu.press_releases', 'Press Releases'), href: "/dashboard/press-releases", adminOnly: true },
    'creators': { label: t('dashboard.header.menu.creators', 'Creators'), href: "/dashboard/creators", adminOnly: true },
    'terms': { label: t('dashboard.header.menu.terms', 'Terms of Use'), href: "/dashboard/terms", adminOnly: true },
    'search-analytics': { label: t('dashboard.header.menu.search_analytics', 'Search Analytics'), href: "/dashboard/search-analytics", adminOnly: true },
    'ai-tutors': { label: t('dashboard.header.menu.ai_tutors', 'AI Tutors'), href: "/dashboard/ai-tutors", adminOnly: true },
    'menus': { label: t('dashboard.header.menu.menus', 'Menus'), href: "/dashboard/menus", adminOnly: true },
    'categories': { label: t('dashboard.header.menu.categories', 'Categories'), href: "/dashboard/categories", adminOnly: true },
  };

  const toggleSubMenu = (label: string) => {
    setOpenSubMenus(prev => 
      prev.includes(label) 
        ? prev.filter(item => item !== label)
        : [...prev, label]
    );
  };

  // Generate menu structure from dashboard preferences
  const getAdminStaffMenuGroups = (): MenuGroup[] => {
    const baseMenu: MenuGroup[] = [
      { 
        label: t('dashboard.header.home'), 
        href: "/dashboard",
        type: "link" as const
      }
    ];

    // If no saved layout, use default order
    if (!layoutDesktop || layoutDesktop.length === 0) {
      return [
        ...baseMenu,
        {
          label: t('dashboard.header.menu.admin_section'),
          type: "dropdown" as const,
          items: [
            { label: t('dashboard.header.menu.users'), href: "/dashboard/users", adminOnly: true },
            { label: t('dashboard.header.menu.teachers'), href: "/dashboard/teachers", adminOnly: true },
            { label: t('dashboard.header.menu.buildings'), href: "/dashboard/buildings" },
            { label: t('dashboard.header.menu.programs'), href: "/dashboard/programs", adminOnly: true },
            { label: t('dashboard.header.menu.products'), href: "/dashboard/products", adminOnly: true },
            { label: t('dashboard.header.menu.coupons'), href: "/dashboard/coupons", adminOnly: true },
            { label: 'Kit Funnel', href: "/dashboard/kit-funnel", adminOnly: true },
            { label: t('dashboard.header.menu.analytics'), href: "/dashboard/analytics", adminOnly: true },
            { label: t('dashboard.header.menu.permissions'), href: "/dashboard/permissions", adminOnly: true },
            { label: t('dashboard.header.menu.lab'), href: "/dashboard/lab", adminOnly: true },
            { label: t('dashboard.header.menu.social_networks', 'Social Networks'), href: "/dashboard/social-networks", adminOnly: true },
            { label: t('dashboard.header.menu.creators', 'Creators'), href: "/dashboard/creators", adminOnly: true },
            { label: t('dashboard.header.menu.terms', 'Terms of Use'), href: "/dashboard/terms", adminOnly: true },
            { label: t('dashboard.header.menu.menus', 'Menus'), href: "/dashboard/menus", adminOnly: true },
            { label: t('dashboard.header.menu.categories', 'Categories'), href: "/dashboard/categories", adminOnly: true },
          ]
        },
        {
          // Merged Academic Management + Student Services (temporary, to keep the header short)
          label: t('dashboard.header.menu.main_menu', 'Main Menu'),
          type: "dropdown" as const,
          items: [
            { label: t('dashboard.header.menu.students'), href: "/dashboard/students" },
            { label: t('dashboard.header.menu.generations'), href: "/dashboard/cohorts", adminOnly: true },
            { label: t('dashboard.header.menu.classes_courses'), href: "/dashboard/classes-courses" },
            { label: t('dashboard.header.menu.attendance'), href: "/dashboard/attendance" },
            { label: t('dashboard.header.menu.announcements', 'Pop-ups Announcements'), href: "/dashboard/announcements", adminOnly: true },
            { label: t('dashboard.header.menu.pdf_library', 'PDF Library'), href: "/dashboard/pdf-library", adminOnly: true },
            { label: t('dashboard.header.menu.press_releases', 'Press Releases'), href: "/dashboard/press-releases", adminOnly: true },
            { label: t('dashboard.header.menu.program_applications'), href: "/dashboard/applications" },
            { label: t('dashboard.header.menu.class_requests'), href: "/dashboard/course-enrollment-requests" },
            { label: t('dashboard.header.menu.subscriptions'), href: "/dashboard/enrollments" },
            { label: t('dashboard.header.menu.grades'), href: "/dashboard/grades" },
            { label: t('dashboard.header.menu.leads'), href: "/dashboard/leads" },
            { label: t('dashboard.header.menu.careers', 'Careers'), href: "/dashboard/careers", adminOnly: true },
            { label: t('dashboard.header.menu.search_analytics', 'Search Analytics'), href: "/dashboard/search-analytics", adminOnly: true },
            { label: t('dashboard.header.menu.ai_tutors', 'AI Tutors'), href: "/dashboard/ai-tutors", adminOnly: true },
          ]
        },
        {
          label: t('dashboard.header.menu.operations_section'),
          type: "dropdown" as const,
          items: [
            { label: t('dashboard.header.menu.tracking'), href: "/dashboard/tasks", adminOnly: true },
            { label: t('dashboard.header.menu.calendar'), href: "/dashboard/school-calendar" },
            { label: t('dashboard.header.menu.communications'), href: "/dashboard/communications" },
            { label: t('dashboard.header.menu.presentations'), href: "/dashboard/presentations", adminOnly: true },
          ]
        },
      ];
    }

    // Build menu from saved layout (excluding deprecated modules)
    const customMenuGroups = layoutDesktop
      .filter((section: any) => section.id !== 'deprecated-section')
      .map((section: any) => {
        const menuLabel = sectionToMenuMap[section.id] || section.title;
        const menuItems = section.cards
          .map((card: any) => cardToMenuItemMap[card.id])
          .filter(Boolean);

        return {
          label: menuLabel,
          type: "dropdown" as const,
          items: menuItems,
        };
      });

    // Merge Academic Management + Student Services into one "Main Menu" dropdown
    const academicLabel = sectionToMenuMap['academic-section'];
    const servicesLabel = sectionToMenuMap['services-section'];
    const mergedItems = customMenuGroups
      .filter(g => g.label === academicLabel || g.label === servicesLabel)
      .flatMap(g => g.items || []);
    const mergedGroups: MenuGroup[] = [];
    let mergedInserted = false;
    customMenuGroups.forEach((g) => {
      if (g.label === academicLabel || g.label === servicesLabel) {
        if (!mergedInserted) {
          mergedGroups.push({
            label: t('dashboard.header.menu.main_menu', 'Main Menu'),
            type: "dropdown" as const,
            items: mergedItems,
          });
          mergedInserted = true;
        }
      } else {
        mergedGroups.push(g);
      }
    });

    return [...baseMenu, ...mergedGroups];
  };

  const adminStaffMenuGroups = getAdminStaffMenuGroups();

  // Mobile menu items (flattened for mobile view) - derived from menu groups
  const getAdminStaffMenuItems = (): MenuItem[] => {
    const items: MenuItem[] = [{ label: t('dashboard.header.home'), href: "/dashboard" }];
    adminStaffMenuGroups.forEach(group => {
      if (group.type === "dropdown" && group.items) {
        items.push(...group.items);
      }
    });
    
    return items;
  };

  const adminStaffMenuItems = getAdminStaffMenuItems();

  const instructorMenuItems = [
    { label: t('dashboard.header.instructor_menu.teachers'), href: "/dashboard" },
    { label: t('dashboard.header.instructor_menu.calendar'), href: "/dashboard/school-calendar" },
    { label: t('dashboard.header.instructor_menu.communications'), href: "/dashboard/communications" },
    { label: t('dashboard.header.instructor_menu.attendance'), href: "/dashboard/attendance" },
  ];

  // Check if student has purchased videos to conditionally show "My Videos"
  const { data: purchaseCount } = useQuery({
    queryKey: ["header-video-purchase-count", user?.id],
    queryFn: async () => {
      if (!user?.id) return 0;
      const { count, error } = await supabase
        .from("video_purchases")
        .select("*", { count: "exact", head: true })
        .eq("user_id", user.id)
        .eq("status", "completed");
      if (error) throw error;
      return count || 0;
    },
    enabled: !!user?.id,
  });
  const hasPurchasedVideos = (purchaseCount ?? 0) > 0;

  // Student menu groups for desktop navigation with dropdowns
  const studentMenuGroups: MenuGroup[] = [
    {
      label: t('dashboard.header.student_menu.home'),
      href: "/student/dashboard",
      type: "link" as const,
      section: "dashboard",
    },
    {
      label: t('dashboard.header.public_menu.library', 'Library'),
      href: "/student/library",
      type: "link" as const,
      section: "dashboard",
    },
    {
      label: t('dashboard.header.student_menu.ai_tutor', 'AI Tutor'),
      href: "/ai-tutor",
      type: "link" as const,
      section: "dashboard",
    },
    {
      label: t('dashboard.header.student_menu.contact', 'Contact'),
      href: "/student/contact",
      type: "link" as const,
      section: "dashboard",
    },

    {
      label: t('dashboard.header.student_menu.my_account', 'My Account'),
      href: "/student/payments",
      type: "link" as const,
      section: "dashboard",
    },
    {
      label: t('dashboard.header.public_menu.creators', 'Creators'),
      href: "/creators",
      type: "link" as const,
      section: "frontend",
    },
    {
      label: t('nav.pricing', 'Pricing'),
      href: "/#pricing",
      type: "link" as const,
      section: "frontend",
    },
    {
      label: t('dashboard.header.public_menu.about', 'About us'),
      href: "/aboutus",
      type: "link" as const,
      section: "frontend",
    },
    {
      label: t('dashboard.header.public_menu.explore', 'Explore'),
      type: "dropdown" as const,
      section: "frontend",
      items: [
        { label: t('dashboard.header.public_menu.kids', 'Kids'), href: "/kids" },
        { label: t('dashboard.header.public_menu.training', 'Training'), href: "/training" },
        { label: t('dashboard.header.public_menu.news', 'News'), href: "/news" },
        { label: t('dashboard.header.public_menu.press', 'Press'), href: "/press" },
      ],
    },
  ];

  // Mobile menu items (flattened for mobile view)
  const studentMenuItems = [
    { label: t('dashboard.header.student_menu.home'), href: "/student/dashboard" },
    { label: t('dashboard.header.student_menu.my_account', 'My Account'), href: "/student/payments" },
    { label: t('dashboard.header.public_menu.library', 'Library'), href: "/student/library" },
    { label: t('dashboard.header.student_menu.ai_tutor', 'AI Tutor'), href: "/ai-tutor" },
    { label: t('dashboard.header.student_menu.contact', 'Contact'), href: "/student/contact" },

  ];

  // Guest menu groups for desktop navigation with dropdowns
  const guestMenuGroups: MenuGroup[] = [
    {
      label: t('dashboard.header.guest_menu.home'),
      href: "/guest/dashboard",
      type: "link" as const,
      section: "dashboard",
    },
    {
      label: t('dashboard.header.public_menu.library', 'Library'),
      href: "/videos",
      type: "link" as const,
      section: "dashboard",
    },
    {
      label: t('dashboard.header.guest_menu.contact', 'Contact'),
      href: "/guest/contact",
      type: "link" as const,
      section: "dashboard",
    },
    {
      label: t('dashboard.header.public_menu.creators', 'Creators'),
      href: "/creators",
      type: "link" as const,
      section: "frontend",
    },
    {
      label: t('nav.pricing', 'Pricing'),
      href: "/#pricing",
      type: "link" as const,
      section: "frontend",
    },
    {
      label: t('dashboard.header.public_menu.about', 'About us'),
      href: "/aboutus",
      type: "link" as const,
      section: "frontend",
    },
    {
      label: t('dashboard.header.public_menu.explore', 'Explore'),
      type: "dropdown" as const,
      section: "frontend",
      items: [
        { label: t('dashboard.header.public_menu.kids', 'Kids'), href: "/kids" },
        { label: t('dashboard.header.public_menu.training', 'Training'), href: "/training" },
        { label: t('dashboard.header.public_menu.news', 'News'), href: "/news" },
        { label: t('dashboard.header.public_menu.press', 'Press'), href: "/press" },
      ],
    },
  ];

  // Mobile menu items (flattened for mobile view)
  const guestMenuItems = [
    { label: t('dashboard.header.guest_menu.home'), href: "/guest/dashboard" },
    { label: t('dashboard.header.public_menu.library', 'Library'), href: "/videos" },
    { label: t('dashboard.header.guest_menu.contact', 'Contact'), href: "/guest/contact" },
  ];

  // DB-managed dashboard menus (admin can reorder/toggle at /dashboard/menus)
  const lang = (i18n.language || "en").startsWith("es") ? "es" : "en";
  const { data: studentMenuTree } = useMenu("student_dashboard");
  const { data: guestMenuTree } = useMenu("guest_dashboard");

  const nodeLabel = (n: MenuItemNode) => (lang === "es" ? n.label_es : n.label_en);
  const toGroups = (nodes: MenuItemNode[]): MenuGroup[] =>
    nodes.map((n) =>
      n.link_type === "group"
        ? {
            label: nodeLabel(n),
            type: "dropdown" as const,
            section: "dashboard" as const,
            items: n.children.map((c) => ({ label: nodeLabel(c), href: c.link_value || "#" })),
          }
        : {
            label: nodeLabel(n),
            type: "link" as const,
            href: n.link_value || "#",
            section: "dashboard" as const,
          },
    );

  const studentGroups: MenuGroup[] =
    studentMenuTree && studentMenuTree.length > 0 ? toGroups(studentMenuTree) : studentMenuGroups;
  const guestGroups: MenuGroup[] =
    guestMenuTree && guestMenuTree.length > 0 ? toGroups(guestMenuTree) : guestMenuGroups;

  const flatten = (groups: MenuGroup[], fallback: { label: string; href: string }[]) => {
    const links = groups
      .filter((g) => g.type === "link" && g.href)
      .map((g) => ({ label: g.label, href: g.href as string }));
    return links.length > 0 ? links : fallback;
  };

  // Institution contacts use the regular learner dashboard when they are on
  // student routes; their staff role only grants access to the institution portal.
  const { data: userInstitution } = useUserInstitution(user?.id);
  const isStaffStudentView =
    location.pathname.startsWith('/student') &&
    userRoles.includes('staff') &&
    !userRoles.includes('admin') &&
    !userRoles.includes('dev');

  // Check if user is pure instructor (not admin/staff)
  const isPureInstructor = userRoles.includes('instructor') && 
    !userRoles.includes('admin') && 
    !userRoles.includes('staff') && 
    !userRoles.includes('dev');

  const isGuest = userRoles.includes('guest');
  const showAdminStaffNavigation = isAdminOrStaff() && !isStaffStudentView;
  const showWebToggle = showAdminStaffNavigation || isPureInstructor || userRoles.includes('dev');

  
  const menuItems = isPureInstructor
    ? instructorMenuItems
    : showAdminStaffNavigation
      ? adminStaffMenuItems.filter(item => 
          !item.adminOnly || userRoles.includes('admin') || userRoles.includes('dev')
        )
      : isGuest
        ? flatten(guestGroups, guestMenuItems)
        : flatten(studentGroups, studentMenuItems);

  const handleSignOut = async () => {
    await signOut();
  };

  const handleMenuNavigate = (href: string) => {
    if (applicationPaths.includes(href)) {
      setComingSoonOpen(true);
    } else {
      navigate(href);
    }
  };

  // Check if user has multiple roles to show switcher
  const hasBothStudentAndStaff = userRoles.includes('student') && 
    (userRoles.includes('admin') || userRoles.includes('staff')) &&
    !isStaffStudentView;

  const handleSwitchDashboard = () => {
    if (location.pathname.startsWith('/student')) {
      navigate('/dashboard');
    } else {
      navigate('/student/dashboard');
    }
  };

  // Institution contacts/members get a switch between the institution stats
  // portal (/staff/institution) and their normal user dashboard.
  const isInstitutionPortal = location.pathname.startsWith('/staff/institution');
  const showInstitutionToggle = !!userInstitution;
  const handleSwitchInstitution = () => {
    if (isInstitutionPortal) {
      navigate('/student/dashboard');
    } else {
      navigate('/staff/institution');
    }
  };

  const LIBRARY_HREFS = new Set(["/videos", "/student/library", "/guest/library"]);

  const renderDesktopGroup = (group: MenuGroup) => {
    if (group.type === "link") {
      if (group.href && LIBRARY_HREFS.has(group.href)) {
        return (
          <LibraryMegaMenuItem
            key={`library-desktop-${group.label}`}
            label={group.label}
            active={location.pathname === group.href}
            isOpen={isLibraryOpen}
            setOpen={setIsLibraryOpen}
            timerRef={libraryCloseTimer}
            mainHref={group.href}
            onNavigate={(path) => navigate(path)}
          />
        );
      }

      return (
        <NavigationMenuItem key={group.href}>
          <button
            onClick={() => navigate(group.href!)}
            className={cn(
              "group inline-flex h-9 w-max items-center justify-center rounded-md bg-background px-2 py-1.5 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none disabled:pointer-events-none disabled:opacity-50",
              location.pathname === group.href && "text-primary"
            )}
          >
            {group.label}
          </button>
        </NavigationMenuItem>
      );
    }
    const filteredItems = group.items || [];
    if (filteredItems.length === 0) return null;
    const isGroupActive = filteredItems.some((item) => location.pathname === item.href);
    return (
      <NavigationMenuItem key={group.label} className="relative">
        <NavigationMenuTrigger className={cn("h-9 px-2 py-1.5", isGroupActive && "text-primary")}>
          {group.label}
        </NavigationMenuTrigger>
        <NavigationMenuContent>
          <ul className="grid w-[200px] gap-2 p-2 bg-background border rounded-md shadow-lg">
            {filteredItems.map((item) => (
              <li key={item.href}>
                <NavigationMenuLink asChild>
                  <button
                    onClick={() => handleMenuNavigate(item.href)}
                    className={cn(
                      "block select-none space-y-1 rounded-md p-2 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground w-full text-left",
                      location.pathname === item.href && "bg-accent text-primary font-medium"
                    )}
                  >
                    <div className="text-sm font-medium leading-none">{item.label}</div>
                  </button>
                </NavigationMenuLink>
              </li>
            ))}
          </ul>
        </NavigationMenuContent>
      </NavigationMenuItem>
    );
  };

  const DesktopSectionDivider = () => (
    <li key="section-divider" role="presentation" aria-hidden="true" className="mx-2 flex items-center pointer-events-none">
      <div className="h-6 w-px bg-border" />
    </li>
  );

  const renderMobileGroup = (group: MenuGroup) => {
    if (group.type === "link") {
      if (group.href && LIBRARY_HREFS.has(group.href)) {
        const mainHref = group.href;
        return (
          <div key="library-mobile" className="border border-border rounded-md overflow-hidden">
            <div className="w-full flex items-center justify-between font-semibold text-foreground">
              <button
                onClick={() => {
                  navigate(mainHref);
                  setIsMenuOpen(false);
                }}
                className={`flex-1 text-left py-3 px-4 hover:bg-accent transition-colors ${
                  location.pathname === mainHref ? 'text-primary' : ''
                }`}
              >

                {group.label}
              </button>
              <button
                onClick={() => setIsLibraryMobileOpen((v) => !v)}
                className="p-3 text-muted-foreground hover:text-primary"
                aria-label="Toggle categories"
                aria-expanded={isLibraryMobileOpen}
              >
                <ChevronDown className={`w-4 h-4 transition-transform ${isLibraryMobileOpen ? "rotate-180" : ""}`} />
              </button>
            </div>
            {isLibraryMobileOpen && (
              <div className="bg-muted/30 border-t border-border p-2">
                <VideoLibraryMegaMenu
                  basePath={mainHref}
                  onNavigate={(path) => {
                    setIsLibraryMobileOpen(false);
                    setIsMenuOpen(false);
                    navigate(path);
                  }}
                />

              </div>
            )}
          </div>
        );
      }
      return (
        <button
          key={group.href}
          onClick={() => {
            navigate(group.href!);
            setIsMenuOpen(false);
          }}
          className={`text-left hover:bg-accent transition-colors font-medium py-3 px-4 rounded-md ${
            location.pathname === group.href ? 'bg-primary text-primary-foreground' : 'text-foreground hover:text-primary'
          }`}
        >
          {group.label}
        </button>
      );
    }
    const filteredItems = group.items || [];
    if (filteredItems.length === 0) return null;
    const isOpen = openSubMenus.includes(group.label);
    return (
      <div key={group.label} className="border border-border rounded-md overflow-hidden">
        <button
          onClick={() => toggleSubMenu(group.label)}
          className="w-full flex items-center justify-between py-3 px-4 font-semibold text-foreground hover:bg-accent transition-colors"
        >
          <span>{group.label}</span>
          {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
        {isOpen && (
          <div className="bg-muted/30 border-t border-border">
            {filteredItems.map((item) => (
              <button
                key={item.href}
                onClick={() => {
                  if (applicationPaths.includes(item.href)) {
                    setComingSoonOpen(true);
                  } else {
                    navigate(item.href);
                  }
                  setIsMenuOpen(false);
                }}
                className={`w-full text-left py-2.5 px-6 text-sm transition-colors ${
                  location.pathname === item.href ? 'bg-primary text-primary-foreground font-medium' : 'text-muted-foreground hover:bg-accent hover:text-primary'
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };


  return (
    <>
    <ApplicationComingSoonDialog open={comingSoonOpen} onOpenChange={setComingSoonOpen} />
    <AllAccessPlansDialog open={plansDialogOpen} onOpenChange={setPlansDialogOpen} />
    <nav className="fixed top-0 left-0 right-0 z-[80] bg-background/95 text-foreground backdrop-blur-sm border-b border-border shadow-soft">
      <div className="container mx-auto px-4 lg:px-8 max-w-full overflow-visible">
        <div className="flex items-center justify-between gap-2 h-14 lg:h-20 min-w-0">
          {/* Logo */}
          <div className="flex items-center gap-2 shrink-0">
            <div 
              className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity shrink-0" 
              onClick={() => {
                const isStudentLike = userRoles.includes('student') || userRoles.includes('guest');
                navigate((isStudentLike || isStaffStudentView) && !showAdminStaffNavigation && !isPureInstructor ? '/student/dashboard' : '/dashboard');
              }}
            >
              <img 
                src={siteSettings?.dashboard_logo_url || "/iAmerica-logo.png"} 
                alt="iAmerica Logo" 
                className="w-10 h-10 object-contain animate-fade-in"
              />
              <div className="flex flex-col animate-fade-in shrink-0">
                <span className="text-base font-bold bg-gradient-primary bg-clip-text text-transparent leading-tight whitespace-nowrap max-w-[9rem] truncate">
                  {siteSettings?.site_name || "iAmerica"}
                </span>
                <span className={`text-xs font-semibold text-muted-foreground leading-tight -mt-0.5 whitespace-nowrap max-w-[9rem] truncate ${showAdminStaffNavigation || isPureInstructor ? '' : 'italic'}`}>
                  {siteSettings?.slogan_dashboard || (showAdminStaffNavigation || isPureInstructor ? "Academy Hub" : "Move Faster")}
                </span>
              </div>
            </div>

            {/* Mobile: Subscribe button between logo and language switcher */}
            {showMobileSubscribe && !isPureInstructor && (
              <button
                onClick={() => setPlansDialogOpen(true)}
                className="lg:hidden ml-2 inline-flex items-center justify-center rounded-md bg-[#fcb424] px-2.5 py-1 text-xs font-semibold text-[#070e1e] hover:bg-[#fcb424]/90 transition-colors"
              >
                {t('dashboard.header.guest_menu.subscribe_short', 'Subscribe')}
              </button>
            )}
          </div>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center gap-0 relative z-[85]">
            {isPureInstructor ? (
              <div className="flex items-center gap-2">
                {instructorMenuItems.map((item) => (
                  <button
                    key={item.href}
                    onClick={() => navigate(item.href)}
                    className={`text-sm text-foreground hover:text-primary transition-colors font-medium ${
                      location.pathname === item.href ? 'text-primary' : ''
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            ) : showAdminStaffNavigation ? (
              <NavigationMenu>
                <NavigationMenuList className="gap-0">
                  {adminStaffMenuGroups.map((group) => {
                    // Filter admin-only items (allow admin and dev)
                    if (group.adminOnly && !userRoles.includes('admin') && !userRoles.includes('dev')) {
                      return null;
                    }

                    if (group.type === "link") {
                      return (
                        <NavigationMenuItem key={group.href}>
                          <button
                            onClick={() => navigate(group.href!)}
                            className={cn(
                              "group inline-flex h-9 w-max items-center justify-center rounded-md bg-background px-2 py-1.5 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none disabled:pointer-events-none disabled:opacity-50",
                              location.pathname === group.href && "text-primary"
                            )}
                          >
                            {group.label}
                          </button>
                        </NavigationMenuItem>
                      );
                    }

                    // Filter admin-only items from dropdown (allow admin and dev)
                    const filteredItems = group.items?.filter(
                      item => !item.adminOnly || userRoles.includes('admin') || userRoles.includes('dev')
                    ) || [];

                    if (filteredItems.length === 0) return null;

                    // Check if any item in dropdown is active
                    const isGroupActive = filteredItems.some(
                      item => location.pathname === item.href
                    );

                    return (
                      <NavigationMenuItem key={group.label} className="relative">
                        <NavigationMenuTrigger className={cn(
                          "h-9 px-2 py-1.5",
                          isGroupActive && "text-primary"
                        )}>
                          {group.label}
                        </NavigationMenuTrigger>
                        <NavigationMenuContent>
                          <ul className="grid w-[200px] gap-2 p-2 bg-background border rounded-md shadow-lg">
                            {filteredItems.map((item) => (
                              <li key={item.href}>
                                <NavigationMenuLink asChild>
                                  <button
                                    onClick={() => navigate(item.href)}
                                    className={cn(
                                      "block select-none space-y-1 rounded-md p-2 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground w-full text-left",
                                      location.pathname === item.href && "bg-accent text-primary font-medium"
                                    )}
                                  >
                                    <div className="text-sm font-medium leading-none">
                                      {item.label}
                                    </div>
                                  </button>
                                </NavigationMenuLink>
                              </li>
                            ))}
                          </ul>
                        </NavigationMenuContent>
                      </NavigationMenuItem>
                    );
                  })}
                </NavigationMenuList>
              </NavigationMenu>
            ) : !isGuest && !isPureInstructor ? (
              // Student menu with dropdowns
              <NavigationMenu>
                <NavigationMenuList className="gap-0">
                  {studentGroups.filter(g => g.section !== "frontend").map(renderDesktopGroup)}
                  {studentGroups.some(g => g.section === "frontend") && <DesktopSectionDivider />}
                  {studentGroups.filter(g => g.section === "frontend").map(renderDesktopGroup)}

                </NavigationMenuList>
              </NavigationMenu>
            ) : isGuest ? (
              // Guest menu with dropdowns
              <NavigationMenu>
                <NavigationMenuList className="gap-0">
                  {guestGroups.filter(g => g.section !== "frontend").map(renderDesktopGroup)}
                  {guestGroups.some(g => g.section === "frontend") && <DesktopSectionDivider />}
                  {guestGroups.filter(g => g.section === "frontend").map(renderDesktopGroup)}

                  {!hasAllAccess && (
                    <NavigationMenuItem>
                      <button
                        onClick={() => setPlansDialogOpen(true)}
                        className="group inline-flex h-9 w-max items-center justify-center rounded-md bg-[#fcb424] px-3 py-1.5 text-sm font-semibold text-[#070e1e] transition-colors hover:bg-[#fcb424]/90 focus:outline-none ml-1"
                      >
                        {t('dashboard.header.guest_menu.subscribe', 'Subscribe to plans')}
                      </button>
                    </NavigationMenuItem>
                  )}
                </NavigationMenuList>
              </NavigationMenu>
            ) : (
              // Other roles - simple menu
              <div className="flex items-center gap-2">
                {menuItems.map((item) => (
                  <button
                    key={item.href}
                    onClick={() => navigate(item.href)}
                    className={`text-sm text-foreground hover:text-primary transition-colors font-medium ${
                      location.pathname === item.href ? 'text-primary' : ''
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="hidden lg:flex items-center gap-[10px] min-w-0 shrink">
            <HeaderSearch variant="desktop" compact />
            <LanguageSwitcher />
            {showWebToggle && (
              <Button 
                onClick={() => navigate('/')}
                variant="ghost"
                size="sm"
                className="gap-1 h-8 px-2 text-xs"
              >
                <Home className="h-3.5 w-3.5" />
                Web
              </Button>
            )}

            
            {hasBothStudentAndStaff && (
              <Button 
                onClick={handleSwitchDashboard}
                variant="outline"
                size="sm"
                className="gap-2"
              >
                <RefreshCcw className="h-4 w-4" />
                {location.pathname.startsWith('/student') ? t('dashboard.header.staff_panel') : t('dashboard.header.student_panel')}
              </Button>
            )}

            {showInstitutionToggle && (
              <Button
                onClick={handleSwitchInstitution}
                variant="outline"
                size="sm"
                className="gap-2"
              >
                <RefreshCcw className="h-4 w-4" />
                {isInstitutionPortal
                  ? t('dashboard.header.student_panel')
                  : t('dashboard.header.institution_panel')}
              </Button>
            )}


            {userRoles.includes('student') && (
              <Button
                onClick={() => navigate('/student/programs')}
                variant="ghost"
                size="sm"
                className="gap-2"
              >
                <GraduationCap className="h-4 w-4" />
                {t('dashboard.header.my_programs', 'My Programs')}
              </Button>
            )}

            
            <Button 
              onClick={handleSignOut}
              size="sm"
              className="gap-2 bg-logout text-white hover:bg-logout-hover"
            >
              <LogOut className="h-4 w-4" />
              {t('dashboard.header.sign_out')}
            </Button>
          </div>

          {/* Mobile: Language switcher + Search icon + Menu Button */}
          <div className="lg:hidden flex items-center gap-1">
            <LanguageSwitcher />
            <button
              onClick={() => setIsMobileSearchOpen(true)}
              className="p-2 text-foreground hover:text-primary transition-colors"
              aria-label={t('dashboard.header.search', 'Search')}
            >
              <Search className="w-5 h-5" />
            </button>
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 text-foreground hover:text-primary transition-colors"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile expanded search overlay (YouTube-style) */}
        {isMobileSearchOpen && (
          <div className="lg:hidden absolute inset-x-0 top-0 h-14 bg-background border-b border-border flex items-center gap-2 px-3 z-[55] animate-fade-in">
            <div className="flex-1">
              <HeaderSearch variant="mobile" onCloseMobile={() => setIsMobileSearchOpen(false)} />
            </div>
            <button
              onClick={() => setIsMobileSearchOpen(false)}
              className="p-2 text-foreground hover:text-primary"
              aria-label={t('common:close', 'Close')}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        )}

      </div>
    </nav>

    {/* Mobile Drawer (right edge) — portaled so #root push-translate doesn't move it */}
    {createPortal(
      <>
        <div
          className={`lg:hidden fixed inset-0 z-[60] transition-opacity duration-300 ${
            isMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
          }`}
          onClick={() => setIsMenuOpen(false)}
          aria-hidden={!isMenuOpen}
        />
        <aside
          className={`lg:hidden fixed top-0 right-0 h-screen w-[85vw] max-w-[320px] z-[70] bg-background border-l border-border shadow-strong transform transition-transform duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] ${
            isMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
          role="dialog"
          aria-modal="true"
          aria-hidden={!isMenuOpen}
        >
          <div className="overflow-y-auto h-screen px-4 pt-5 pb-4">
            <div className="flex flex-col gap-2">
              {/* Social networks + blue iAmerica logo at top of mobile menu */}
              <div className="flex lg:hidden items-center justify-between gap-2 pb-4 border-b border-border text-primary">
                <SocialIcons size={18} iconClassName="h-9 w-9 hover:bg-primary/10" />
                <img
                  src={siteSettings?.dashboard_logo_url || "/iamerica-logo.svg"}
                  alt="iAmerica"
                  className="w-9 h-9 object-contain"
                />
              </div>

              {/* Admin/Staff/Dev/Maestro Menu with Sub-menus */}
              {(showAdminStaffNavigation || isPureInstructor) && !isGuest ? (
                <>
                  {isPureInstructor ? (
                    instructorMenuItems.map((item) => (
                      <button
                        key={item.href}
                        onClick={() => {
                          navigate(item.href);
                          setIsMenuOpen(false);
                        }}
                        className={`text-left hover:bg-accent transition-colors font-medium py-3 px-4 rounded-md ${
                          location.pathname === item.href ? 'bg-primary text-primary-foreground' : 'text-foreground hover:text-primary'
                        }`}
                      >
                        {item.label}
                      </button>
                    ))
                  ) : (
                    adminStaffMenuGroups.map((group) => {
                      if (group.adminOnly && !userRoles.includes('admin') && !userRoles.includes('dev')) {
                        return null;
                      }
                      if (group.type === "link") {
                        return (
                          <button
                            key={group.href}
                            onClick={() => {
                              navigate(group.href!);
                              setIsMenuOpen(false);
                            }}
                            className={`text-left hover:bg-accent transition-colors font-medium py-3 px-4 rounded-md ${
                              location.pathname === group.href ? 'bg-primary text-primary-foreground' : 'text-foreground hover:text-primary'
                            }`}
                          >
                            {group.label}
                          </button>
                        );
                      }
                      const filteredItems = group.items?.filter(
                        item => !item.adminOnly || userRoles.includes('admin') || userRoles.includes('dev')
                      ) || [];
                      if (filteredItems.length === 0) return null;
                      const isOpen = openSubMenus.includes(group.label);
                      return (
                        <div key={group.label} className="border border-border rounded-md overflow-hidden">
                          <button
                            onClick={() => toggleSubMenu(group.label)}
                            className="w-full flex items-center justify-between py-3 px-4 font-semibold text-foreground hover:bg-accent transition-colors"
                          >
                            <span>{group.label}</span>
                            {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          </button>
                          {isOpen && (
                            <div className="bg-muted/30 border-t border-border">
                              {filteredItems.map((item) => (
                                <button
                                  key={item.href}
                                  onClick={() => {
                                    navigate(item.href);
                                    setIsMenuOpen(false);
                                  }}
                                  className={`w-full text-left py-2.5 px-6 text-sm transition-colors ${
                                    location.pathname === item.href ? 'bg-primary text-primary-foreground font-medium' : 'text-muted-foreground hover:bg-accent hover:text-primary'
                                  }`}
                                >
                                  {item.label}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </>
              ) : (
                <>
                  {/* Mobile CTAs: Suggestion + Referral moved from dashboard body */}
                  {!isGuest && user && (
                    <div className="flex flex-col gap-1 pb-2 [&_button]:w-full">
                      <span className="px-1 text-xs text-muted-foreground">
                        {t('dashboard.header.mobile_suggest_subtitle', 'Suggest a new class')}
                      </span>
                      <ClassSuggestionButton compact />
                      <ReferralChip userId={user.id} compact />
                    </div>
                  )}

                  {(() => {
                    const groups = isGuest ? guestGroups : studentGroups;
                    const dashGroups = groups.filter(g => g.section !== "frontend");
                    const frontGroups = groups.filter(g => g.section === "frontend");
                    return (
                      <>
                        {dashGroups.map(renderMobileGroup)}
                        {frontGroups.length > 0 && (
                          <div className="mt-2 pt-2 border-t border-border">
                            <div className="border border-primary/30 rounded-md overflow-hidden">
                              <button
                                onClick={() => setIsFrontendMobileOpen((v) => !v)}
                                className="w-full flex items-center justify-between py-3 px-4 font-semibold text-foreground bg-muted/40 hover:bg-accent transition-colors"
                                aria-expanded={isFrontendMobileOpen}
                              >
                                <span>{t('dashboard.header.mobile_frontend_section', 'Explore site')}</span>
                                {isFrontendMobileOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                              </button>
                              {isFrontendMobileOpen && (
                                <div className="bg-muted/20 border-t border-border p-2 flex flex-col gap-2">
                                  {frontGroups.map(renderMobileGroup)}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}



                  {isGuest && !hasAllAccess && (
                    <button
                      onClick={() => {
                        setPlansDialogOpen(true);
                        setIsMenuOpen(false);
                      }}
                      className="text-left bg-[#fcb424] text-[#070e1e] hover:bg-[#fcb424]/90 transition-colors font-semibold py-3 px-4 rounded-md"
                    >
                      {t('dashboard.header.guest_menu.subscribe', 'Subscribe to plans')}
                    </button>
                  )}
                </>
              )}

              {/* Footer Actions */}
              <div className="flex flex-col gap-2 pt-4 mt-2 border-t border-border">
                <div className="flex items-center justify-between px-2">
                  <LanguageSwitcher />
                </div>
                {showWebToggle && (
                  <Button
                    onClick={() => {
                      navigate('/');
                      setIsMenuOpen(false);
                    }}
                    variant="ghost"
                    className="w-full gap-2 justify-start"
                  >
                    <Home className="h-4 w-4" />
                    Web
                  </Button>
                )}

                {hasBothStudentAndStaff && (
                  <Button
                    onClick={() => {
                      handleSwitchDashboard();
                      setIsMenuOpen(false);
                    }}
                    variant="outline"
                    className="w-full gap-2 justify-start"
                  >
                    <RefreshCcw className="h-4 w-4" />
                    {location.pathname.startsWith('/student') ? t('dashboard.header.staff_panel') : t('dashboard.header.student_panel')}
                  </Button>
                )}
                {showInstitutionToggle && (
                  <Button
                    onClick={() => {
                      handleSwitchInstitution();
                      setIsMenuOpen(false);
                    }}
                    variant="outline"
                    className="w-full gap-2 justify-start"
                  >
                    <RefreshCcw className="h-4 w-4" />
                    {isInstitutionPortal
                      ? (userRoles.includes('student') ? t('dashboard.header.student_panel') : t('dashboard.header.user_panel', 'My Dashboard'))
                      : t('dashboard.header.institution_panel')}
                  </Button>
                )}
                <Button
                  onClick={handleSignOut}
                  className="w-full gap-2 justify-start bg-logout text-white hover:bg-logout-hover"
                >
                  <LogOut className="h-4 w-4" />
                  {t('dashboard.header.sign_out')}
                </Button>
              </div>

              {/* Mobile back-to-page button */}
              <Button
                type="button"
                variant="default"
                onClick={() => setIsMenuOpen(false)}
                className="self-start mt-2 rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                aria-label="Close menu"
              >
                <ChevronsLeft className="w-4 h-4" />
                {t("common:back", "Back")}
              </Button>
            </div>
          </div>
        </aside>
      </>,
      document.body
    )}
    <InstitutionPausedNotice />
    </>
  );
};

type LibraryMegaMenuItemProps = {
  label: string;
  active: boolean;
  isOpen: boolean;
  setOpen: (v: boolean) => void;
  timerRef: React.MutableRefObject<number | null>;
  onNavigate: (path: string) => void;
  mainHref?: string;
};

const LibraryMegaMenuItem = ({ label, active, isOpen, setOpen, timerRef, onNavigate, mainHref = "/videos" }: LibraryMegaMenuItemProps) => {
  const openMenu = () => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setOpen(true);
  };
  const scheduleClose = () => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => setOpen(false), 180);
  };
  return (
    <NavigationMenuItem
      className="relative"
      onMouseEnter={openMenu}
      onMouseLeave={scheduleClose}
    >
      <button
        onClick={() => {
          setOpen(false);
          onNavigate(mainHref);
        }}
        className={cn(
          "group inline-flex h-9 w-max items-center justify-center gap-1 rounded-md bg-background px-2 py-1.5 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none",
          active && "text-primary"
        )}
        aria-haspopup="true"
        aria-expanded={isOpen}
      >

        {label}
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>
      {isOpen && (
        <div
          className="absolute left-1/2 -translate-x-1/2 top-full pt-3 z-[95]"
          onMouseEnter={openMenu}
          onMouseLeave={scheduleClose}
        >
          <VideoLibraryMegaMenu
            basePath={mainHref}
            onNavigate={(path) => {
              setOpen(false);
              onNavigate(path);
            }}
          />

        </div>
      )}
    </NavigationMenuItem>
  );
};

export default DashboardHeader;
